import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';

export default function MissingOrdersModal({ open, onClose, missingOrdersData }) {
  if (!missingOrdersData) {
    return null;
  }

  const { school_name, days } = missingOrdersData;

  // Filter days to only show those with missing orders
  const daysWithMissingOrders = Object.entries(days).filter(([date, dayData]) => {
    return dayData.missing_orders.count > 0 && 
           Object.keys(dayData.missing_orders.students_by_class).length > 0;
  });

  // Calculate total missing orders across filtered days
  const totalMissingOrders = daysWithMissingOrders.reduce((total, [date, day]) => {
    return total + day.missing_orders.count;
  }, 0);

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#058240',
          color: 'white',
          py: 3,
          px: 3
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="span">
            Missing Orders - {school_name}
          </Typography>
          <Chip 
            label={`Total: ${totalMissingOrders} students`}
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)'
            }}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ maxHeight: '70vh', overflow: 'auto', p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: '0.9rem' }}>
        </Typography>
        
        {daysWithMissingOrders.map(([date, dayData]) => (
          <Accordion 
            key={date} 
            defaultExpanded 
            sx={{ 
              mb: 2,
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              '&:before': { display: 'none' },
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                backgroundColor: '#f8f9fa',
                borderRadius: '4px 4px 0 0',
                '&.Mui-expanded': {
                  borderRadius: '4px 4px 0 0'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <CalendarTodayIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  {formatDate(date)}
                </Typography>
                <Chip 
                  label={dayData.missing_orders.count}
                  color="primary"
                  size="small"
                  sx={{ 
                    minWidth: 40
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2, pb: 2 }}>
              {Object.entries(dayData.missing_orders.students_by_class).map(([className, students]) => (
                <Accordion 
                  key={`${date}-${className}`} 
                  sx={{ 
                    mb: 1.5, 
                    boxShadow: 'none', 
                    border: '1px solid #f0f0f0',
                    borderRadius: 1,
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      minHeight: 'auto',
                      backgroundColor: '#fafafa',
                      '& .MuiAccordionSummary-content': { margin: '10px 0' }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <GroupIcon color="primary" fontSize="small" />
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {className}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {students.length} students
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, pl: 4, pr: 2 }}>
                    <List dense>
                      {students.map((student, index) => (
                        <React.Fragment key={student.student_id}>
                          <ListItem sx={{ pl: 0, py: 0.75 }}>
                            <ListItemText
                              primary={
                                <Typography variant="body2">
                                  {student.student_name}
                                </Typography>
                              }
                            />
                          </ListItem>
                          {index < students.length - 1 && (
                            <Divider sx={{ ml: 0, mr: 0, borderColor: '#f0f0f0' }} />
                          )}
                        </React.Fragment>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
        
        {daysWithMissingOrders.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No missing orders for this school during the selected period.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#f8f9fa' }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          color="primary"
          sx={{ 
            px: 4,
            py: 1,
            borderRadius: 1.5
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
} 